import React, { useCallback, useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { ChevronRight, ErrorOutline, ExpandMore, OpenInNew } from '@mui/icons-material'
import { Box, Button, Chip, IconButton, Skeleton, Stack, TableCell, TableRow, Typography } from '@mui/material'
import { alpha, Theme, useTheme } from '@mui/material/styles'

import { BlueprintApiItem, useGetTrackedEventQuery, useGetTrackedGameLiveEventBlueprintsQuery } from '../../../../api/core'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { formatDate } from '../../../../helpers/date'
import { sanitizeHtml } from '../../../../helpers/dom'
import { useIsTextTruncated } from '../../../../hooks/useIsTextTruncated'
import { LockedGameSummary } from '../../../../types/LockedFeature'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { UserLanguage } from '../../../account/types/User'
import GameIcon from '../../../game/components/GameIcon/GameIcon'
import { AppType, GamePlatform } from '../../../game/types/Game'
import { LockedGameIndicator } from '../../../live-events/components/LockedGameIndicator/LockedGameIndicator'
import { useLiveEventTagGroupsMapByEventTypeId } from '../../../live-events/hooks/useLiveEventTagGroups'
import { LiveEventEvolutionEntry } from '../../../live-events/types/LiveEventCollection'
import { TrackedGame } from '../../../live-events/types/TrackedGame'
import { useBlueprintCalendarUrlResolver } from '../../hooks/useBlueprintCalendarUrlResolver'
import { useBlueprintDetails, useRetryBlueprintDetails } from '../../hooks/useBlueprintDetails'
import { useBlueprintEventKpis } from '../../hooks/useBlueprintEventKpis'
import { EVENT_ROW_ICON_SIZE } from '../BlueprintEventRowActions/BlueprintEventRowActions'
import { DrilldownEventRow } from '../DrilldownEventRow/DrilldownEventRow'
import { StatusDot } from '../EventStatusDot/EventStatusDot'
import {
  BlueprintEventFilter,
  BlueprintEventRow,
  BlueprintEventSort,
  GAP_WARNING_THRESHOLD_DAYS,
  aggregateGameEvents,
  buildBlueprintEventRows,
  earliestVisibleRowStart,
  filterBlueprintEventRows,
  sortBlueprintEventRows,
  summarizeBlueprintEventRows,
} from '../helpers/blueprintEventRows'
import { latestEvolutionEntry } from '../helpers/blueprintEvolutionData'
import { buildLiveEventsBlueprintModalUrl, buildLiveEventsCalendarUrl } from '../helpers/calendarLinks'
import {
  DRILLDOWN_CELL_CLASS,
  DRILLDOWN_COLUMN_COUNT,
  EVENT_ROW_HEIGHT,
  GAME_ICON_GAP,
  GAME_ICON_RADIUS,
  GAME_ICON_SIZE,
  GAME_ROW_HEIGHT,
  GROUP_ROW_HEIGHT,
  eventDividerColor,
  expandedBlockRowSx,
  gameDividerColor,
} from '../helpers/drilldownTableLayout'
import { getTranslatedName } from '../helpers/metricUtils'

// One adopting game with the game-level blueprints it implements.
export interface GameGroup {
  gameId: string
  name: string
  artist: string
  iconUrl?: string
  appType?: AppType
  platforms?: GamePlatform[]
  eventsCount: number
  // US grossing rank from the BE payload; absent = unranked (shown as an em dash).
  srank?: number
  blueprints: BlueprintApiItem[]
  // Outside the user's plan (`gameAllowed: false`). Listed with its counts, but the backend
  // sends no events for it, so it opens the unlock dialog instead of expanding.
  isLocked?: boolean
}

// Read off the rows' own `gameDiscontinued`; a game whose rows all omit it is active,
// since absence of the flag is not evidence of retirement.
export const isGameGroupDiscontinued = (group: GameGroup): boolean => group.blueprints.some((item) => item.gameDiscontinued)

const GAME_ICON_DISCONTINUED_RADIUS = '22%'

// How many event rows an expanded game shows before "Show N more".
export const DEFAULT_VISIBLE_EVENT_ROWS = 8

const EMPTY_ROWS: BlueprintEventRow[] = []
const EMPTY_EVOLUTION_ENTRIES: LiveEventEvolutionEntry[] = []

const emptyCells = (count: number) => Array.from({ length: count }, (_, index) => <TableCell key={index} />)

// Inline text buttons (retry, show more) sized to the rows' secondary copy.
export const inlineTextButtonSx = { p: 0, minWidth: 0, fontSize: (theme: Theme) => theme.typography.h4.fontSize } as const

/**
 * Clicking anywhere on an expandable row is a mouse shortcut for its toggle button.
 * Clicks that land on a control inside the row (the toggle itself, a link, a retry
 * button) belong to that control and must not toggle the row as well.
 */
export const isNestedControlClick = (event: React.MouseEvent<HTMLElement>) => {
  const control = (event.target as Element).closest('a, button, input, [role="button"]')
  return control !== null && control !== event.currentTarget && event.currentTarget.contains(control)
}

interface DrilldownRowToggleProps {
  expanded: boolean
  // Space-separated ids of the rows the toggle reveals; only referenced while they are rendered.
  controls?: string
  label: string
  onToggle: () => void
  children: React.ReactNode
}

/** The disclosure button of an expandable drilldown row; owns the keyboard and screen-reader semantics. */
export const DrilldownRowToggle: React.FC<DrilldownRowToggleProps> = ({ expanded, controls, label, onToggle, children }) => (
  <IconButton
    size="small"
    aria-expanded={expanded}
    aria-controls={expanded && controls ? controls : undefined}
    aria-label={label}
    onClick={onToggle}
    sx={{ p: 0 }}
  >
    {children}
  </IconButton>
)

const DisclosureIcon: React.FC<{ expanded: boolean }> = ({ expanded }) =>
  expanded ? <ExpandMore fontSize="small" sx={{ color: 'primary.main' }} /> : <ChevronRight fontSize="small" sx={{ color: 'text.disabled' }} />

const blueprintDisplayName = (item: BlueprintApiItem, userLanguage: UserLanguage) =>
  item.names ? getTranslatedName(item.names, userLanguage) || Object.values(item.names).find(Boolean) || '' : ''

// Analyst-written HTML description of a game-level blueprint (GR-2916), shown
// as the tooltip of its tag / group row. `descriptions` is market-locale keyed
// like `names`, so fall back to any value; DOMPurify.sanitize is expensive, so
// callers memoize on the raw map.
const blueprintDescriptionHtml = (item: BlueprintApiItem, userLanguage: UserLanguage) => {
  const resolved = item.descriptions ? getTranslatedName(item.descriptions, userLanguage) || Object.values(item.descriptions).find(Boolean) || '' : ''
  return resolved ? sanitizeHtml(resolved) : ''
}

const BlueprintDescription: React.FC<{ html: string }> = ({ html }) => (
  <Box sx={{ typography: 'body2', '& p': { m: 0 }, '& p + p': { mt: 0.75 } }} dangerouslySetInnerHTML={{ __html: html }} />
)

/** Small light-purple tag naming a game-level blueprint; hover shows its analyst description when there is one. */
const BlueprintTag: React.FC<{ name: string; descriptionHtml: string }> = ({ name, descriptionHtml }) => {
  const chip = (
    <Chip size="small" label={name} sx={(theme) => ({ m: 0, bgcolor: alpha(theme.palette.primary.main, 0.14), color: 'primary.dark', flexShrink: 0 })} />
  )
  return descriptionHtml ? (
    <GRTooltip content={<BlueprintDescription html={descriptionHtml} />} styles={{ display: 'inline-flex', flexShrink: 0 }}>
      {chip}
    </GRTooltip>
  ) : (
    chip
  )
}

interface BlueprintEventGroupRowsProps {
  // DOM id of the group row, which the game row's toggle points at.
  rowId: string
  item: BlueprintApiItem
  name: string
  // Every row of this blueprint that passes the filter, in display order. The
  // block caps itself at `DEFAULT_VISIBLE_EVENT_ROWS` until "show more", so the
  // reveal only ever lists this blueprint's own iterations.
  rows: BlueprintEventRow[]
  // Every row of this blueprint whatever the filter, which the KPI request spans so that changing the filter does not refetch it.
  allRows: BlueprintEventRow[]
  // Event instances this blueprint has under the active filter — what the group row
  // counts, so it agrees with the rows beneath it rather than quoting the full history.
  eventInstanceCount: number
  expanded: boolean
  onToggle: (blueprintId: string) => void
  // Its events request failed: the row offers a retry instead of a count and opens into nothing.
  loadFailed: boolean
  onRetry: (blueprintId: string) => void
  isDiscontinued: boolean
  descriptionHtml: string
  maxDurationDays: number
  gameId: string
  eventTypeId: string
  trackedGame?: TrackedGame
  userLanguage: UserLanguage
  // The game is outside the user's plan: the row is listed with its count but opens into
  // nothing and links nowhere; clicking it explains the lock instead.
  locked?: boolean
  onLockedClick?: () => void
}

/**
 * One game-level blueprint inside an expanded game: a slim group row naming it,
 * the event rows it opens into, and its own "show more" / longest-break footer.
 * Its event rows load their own info-popup data from the event getter; the blueprint's evolution,
 * fetched here, supplies the popup's NEW and first-appearance flags, and a mobile game's performance
 * window, also fetched here (one request for the whole block), supplies the popup's KPI chips.
 */
const BlueprintEventGroupRows: React.FC<BlueprintEventGroupRowsProps> = ({
  rowId,
  item,
  name,
  rows,
  allRows,
  eventInstanceCount,
  expanded: expandedProp,
  onToggle,
  loadFailed,
  onRetry,
  isDiscontinued,
  descriptionHtml,
  maxDurationDays,
  gameId,
  eventTypeId,
  trackedGame,
  userLanguage,
  locked = false,
  onLockedClick,
}) => {
  const expanded = expandedProp && !loadFailed && !locked
  const { t } = useTranslation()
  const theme = useTheme()
  const [showAll, setShowAll] = useState(false)

  const visibleRows = showAll ? rows : rows.slice(0, DEFAULT_VISIBLE_EVENT_ROWS)
  const hiddenCount = rows.length - visibleRows.length
  const eventRowIds = visibleRows.map((_, index) => `${rowId}-event-${index}`)
  const summary = useMemo(() => summarizeBlueprintEventRows(rows), [rows])
  const handleToggle = useCallback(() => onToggle(item.id), [onToggle, item.id])
  const handleRetry = useCallback(() => onRetry(item.id), [onRetry, item.id])

  // Calendar deep-link for the group row: resolve the blueprint's newest event on intent only. A family
  // under no event type has no type id to link with, so its type is read off the event and the
  // event rows' links need it as soon as they are on screen.
  const [prefetchCalendar, setPrefetchCalendar] = useState(false)
  const handleCalendarIntent = useCallback(() => setPrefetchCalendar(true), [])
  const needsEventTypeLookup = !eventTypeId
  const calendarDataRequested = prefetchCalendar || (needsEventTypeLookup && expanded)
  // Blueprint evolution serves the calendar link: fetch it as soon as the block opens so a click finds it loaded.
  const {
    data: blueprintEvolution,
    isError: evolutionFailed,
    isUninitialized: evolutionUninitialized,
  } = useGetTrackedGameLiveEventBlueprintsQuery({ blueprintId: item.id, type: 'all' }, { skip: !item.id || (!expanded && !calendarDataRequested) })
  const tagGroupsByEventTypeId = useLiveEventTagGroupsMapByEventTypeId()

  // The info popup's NEW and first-appearance flags. The event getter carries neither `highlighted` nor a
  // first-instance flag for the exact instance, but the blueprint's evolution entries (already loaded above
  // for the calendar link) carry both, as the Blueprint tab reads them, so no game-wide tracked-events
  // search is needed. `undefined` until the evolution arrives; a failed or never-started request (a blueprint
  // with no id skips the query) settles to no entries so the popup falls back to the getter instead of waiting forever.
  const evolutionSettledEmpty = evolutionFailed || evolutionUninitialized || !item.id
  const evolutionEntriesByEventId = useMemo(() => {
    if (!blueprintEvolution) return evolutionSettledEmpty ? new Map<string, LiveEventEvolutionEntry[]>() : undefined
    const byEventId = new Map<string, LiveEventEvolutionEntry[]>()
    blueprintEvolution.evolution.forEach((entry) => {
      if (!entry.eventId) return
      byEventId.set(entry.eventId, [...(byEventId.get(entry.eventId) ?? []), entry])
    })
    return byEventId
  }, [blueprintEvolution, evolutionSettledEmpty])

  // The popup's KPI chips come from the game's daily performance, mobile games only: the endpoint answers 400 for
  // PC/console games. Started on expand, like the getter data, so the first hover finds it loaded or in flight.
  const eventKpis = useBlueprintEventKpis({ gameId, rows: allRows, enabled: expanded && Boolean(trackedGame?.game?.isMobileGame()) })

  const representativeEventId = latestEvolutionEntry(blueprintEvolution?.evolution)?.eventId
  const { data: representativeEvent, isError: eventLookupFailed } = useGetTrackedEventQuery(representativeEventId ?? '', {
    skip: !representativeEventId || !needsEventTypeLookup || !calendarDataRequested,
  })
  const resolvedEventTypeId = eventTypeId || representativeEvent?.event?.typeId || ''
  const blueprintModalPath =
    representativeEventId && resolvedEventTypeId
      ? buildLiveEventsBlueprintModalUrl({ gameId, eventTypeId: resolvedEventTypeId, eventId: representativeEventId, evolutionScope: 'all' })
      : undefined
  const calendarPath = blueprintModalPath ?? buildLiveEventsCalendarUrl({ gameId })
  // The link reads as the blueprint view while it is still resolving (a click waits for it) and until the final URL is known
  // to be the plain game calendar: no blueprint id, a failed or empty evolution, or an event whose type is not found.
  const eventTypeUnresolved =
    Boolean(representativeEventId) && needsEventTypeLookup && (eventLookupFailed || (Boolean(representativeEvent) && !resolvedEventTypeId))
  const opensCalendarOnly =
    !blueprintModalPath && (!item.id || evolutionFailed || (Boolean(blueprintEvolution) && !representativeEventId) || eventTypeUnresolved)
  const calendarLinkLabel = t(opensCalendarOnly ? 'market-analytics:open_in_live_events_calendar' : 'market-analytics:open_event_in_calendar')

  // A click that beats the data would otherwise open the plain calendar. Hold the click, open the tab
  // in the same gesture (so popup blockers allow it) and send it to the Event dialog once resolved.
  // Modified clicks keep the browser's own link handling, and a tab the user closed meanwhile is left alone.
  const resolveCalendarUrl = useBlueprintCalendarUrlResolver()
  const handleCalendarClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      if (blueprintModalPath || event.metaKey || event.ctrlKey || event.shiftKey) return
      const pendingTab = window.open('', '_blank')
      if (!pendingTab) return
      event.preventDefault()
      pendingTab.opener = null
      resolveCalendarUrl({ blueprintId: item.id, gameId, eventTypeId }).then((url) => {
        if (!pendingTab.closed) pendingTab.location.href = url
      })
    },
    [blueprintModalPath, resolveCalendarUrl, item.id, gameId, eventTypeId]
  )

  return (
    <>
      <TableRow
        id={rowId}
        data-testid="drilldown-group-row"
        onClick={
          loadFailed
            ? undefined
            : (event) => {
                if (isNestedControlClick(event)) return
                if (locked) onLockedClick?.()
                else handleToggle()
              }
        }
        sx={(theme) => ({
          ...expandedBlockRowSx(theme),
          height: GROUP_ROW_HEIGHT,
          cursor: loadFailed ? 'default' : 'pointer',
          '& > td': { borderBottomColor: eventDividerColor(theme) },
          '&:hover': loadFailed ? {} : { bgcolor: alpha(theme.palette.primary.main, 0.06) },
        })}
      >
        <TableCell className={DRILLDOWN_CELL_CLASS.eventNumber}>
          {loadFailed ? (
            <ErrorOutline fontSize="small" sx={{ color: 'warning.main' }} />
          ) : locked ? (
            // A flex wrapper: the indicator is an inline-flex span, which would sit on the cell's text baseline.
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <LockedGameIndicator fontSize={16} />
            </Box>
          ) : (
            <DrilldownRowToggle
              expanded={expanded}
              controls={eventRowIds.join(' ')}
              label={t('market-analytics:drilldown_toggle_events_aria', { name: name || t('market-analytics:detail_panel_games_using_unknown_game') })}
              onToggle={handleToggle}
            >
              <DisclosureIcon expanded={expanded} />
            </DrilldownRowToggle>
          )}
        </TableCell>
        <TableCell colSpan={DRILLDOWN_COLUMN_COUNT - 2} className={DRILLDOWN_CELL_CLASS.eventName}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <BlueprintTag name={name || t('market-analytics:detail_panel_games_using_unknown_game')} descriptionHtml={descriptionHtml} />
            {loadFailed ? (
              <>
                <Typography variant="caption" color="warning.main" noWrap>
                  {`· ${t('market-analytics:event_table_blueprint_load_error')}`}
                </Typography>
                <Button variant="text" size="small" onClick={handleRetry} sx={inlineTextButtonSx}>
                  {t('market-analytics:retry')}
                </Button>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }} noWrap>
                {locked
                  ? `· ${t('market-analytics:detail_panel_games_using_event_count', { count: item.liveEventsCount ?? 0 })}`
                  : `· ${t('market-analytics:detail_panel_games_using_event_instance_count', { count: eventInstanceCount })}`}
              </Typography>
            )}
            {isDiscontinued ? (
              <GRTooltip content={t('market-analytics:drilldown_discontinued_tooltip')} styles={{ display: 'inline-flex', flexShrink: 0 }}>
                <Chip size="small" color="warning" variant="outlined" label={t('market-analytics:drilldown_discontinued_chip')} sx={{ m: 0 }} />
              </GRTooltip>
            ) : null}
          </Stack>
        </TableCell>
        <TableCell align="right" className={DRILLDOWN_CELL_CLASS.actions}>
          {locked ? null : (
            <GRTooltip content={calendarLinkLabel} styles={{ display: 'inline-flex' }}>
              <IconButton
                component={RouterLink}
                to={calendarPath}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                aria-label={calendarLinkLabel}
                onMouseEnter={handleCalendarIntent}
                onFocus={handleCalendarIntent}
                onClick={handleCalendarClick}
                sx={{ color: 'primary.main', p: 0 }}
              >
                <OpenInNew sx={{ fontSize: EVENT_ROW_ICON_SIZE }} />
              </IconButton>
            </GRTooltip>
          )}
        </TableCell>
      </TableRow>
      {(expanded ? visibleRows : []).map((row, index) => {
        const { event } = row
        return (
          <DrilldownEventRow
            key={row.key}
            id={eventRowIds[index]}
            row={row}
            index={index + 1}
            maxDurationDays={maxDurationDays}
            userLanguage={userLanguage}
            gameId={gameId}
            // The Event dialog loads the event by id and takes its type from the event itself; the type in the
            // link is only echoed into the dialog's own tab/game switches. A family with no type (Uncategorized)
            // can therefore link every instance with the event's own type, or the one resolved from the
            // blueprint's newest event until that has arrived.
            eventTypeId={eventTypeId}
            fallbackEventTypeId={resolvedEventTypeId}
            trackedGame={trackedGame}
            evolutionEntries={evolutionEntriesByEventId ? evolutionEntriesByEventId.get(event.eventId) ?? EMPTY_EVOLUTION_ENTRIES : undefined}
            tagGroupsByEventTypeId={tagGroupsByEventTypeId}
            popupBgColor={theme.palette.secondary.main}
            kpi={eventKpis.get(row.key)}
          />
        )
      })}
      {expanded && (hiddenCount > 0 || showAll || summary.longestBreak) ? (
        <TableRow
          data-testid="drilldown-block-footer"
          sx={(theme) => ({ ...expandedBlockRowSx(theme), height: EVENT_ROW_HEIGHT, '& > td': { borderBottomColor: gameDividerColor(theme) } })}
        >
          <TableCell className={DRILLDOWN_CELL_CLASS.rank} />
          <TableCell colSpan={DRILLDOWN_COLUMN_COUNT - 1} className={DRILLDOWN_CELL_CLASS.eventName}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={2.5} rowGap={0.5}>
              {hiddenCount > 0 || showAll ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    setShowAll((current) => !current)
                  }}
                  sx={inlineTextButtonSx}
                >
                  {showAll ? t('market-analytics:event_table_show_fewer') : t('market-analytics:event_table_show_more', { count: hiddenCount })}
                </Button>
              ) : null}
              {summary.longestBreak ? (
                <Typography variant="caption" color="text.secondary" component="span">
                  {t('market-analytics:event_table_footer_longest_break')}{' '}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      color: summary.longestBreak.days > GAP_WARNING_THRESHOLD_DAYS ? 'warning.main' : 'text.primary',
                    }}
                  >
                    {t('market-analytics:event_table_days_short', { count: summary.longestBreak.days })}
                  </Box>
                  {summary.longestBreak.fromName && summary.longestBreak.toName
                    ? ` ${t('market-analytics:event_table_footer_break_range', {
                        from: summary.longestBreak.fromName,
                        to: summary.longestBreak.toName,
                      })}`
                    : ''}
                </Typography>
              ) : null}
            </Stack>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}

/**
 * Game-row aggregate: a header-styled "AVG" tag ahead of the value, so the
 * cell reads as a unit label rather than as another data point. The app has no
 * established average glyph — every other screen spells it out.
 */
const AverageDays: React.FC<{ days: number }> = ({ days }) => {
  const { t } = useTranslation()
  return (
    <Stack direction="row" alignItems="baseline" spacing={0.5}>
      <Box component="span" sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.disabled' }}>
        {t('common:avg')}
      </Box>
      <Typography variant="caption" color="text.secondary" component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {t('market-analytics:event_table_days_short', { count: days })}
      </Typography>
    </Stack>
  )
}

/** Placeholder event rows while a game's blueprint details load. */
const EventRowsSkeleton: React.FC<{ rowCount?: number }> = ({ rowCount = 3 }) => (
  <>
    {Array.from({ length: rowCount }, (_, index) => (
      <TableRow
        key={index}
        aria-busy="true"
        sx={(theme) => ({ ...expandedBlockRowSx(theme), height: EVENT_ROW_HEIGHT, '& > td': { borderBottomColor: eventDividerColor(theme) } })}
      >
        <TableCell className={DRILLDOWN_CELL_CLASS.rank} />
        <TableCell className={DRILLDOWN_CELL_CLASS.eventName}>
          <Skeleton variant="text" width="40%" />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={80} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={80} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={56} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={28} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={48} />
        </TableCell>
        <TableCell />
      </TableRow>
    ))}
  </>
)

export interface DrilldownGameRowsProps {
  // DOM id of the game row, which the section band's toggle points at.
  rowId?: string
  blueprintId: string
  blueprintName: string
  eventTypeId: string
  group: GameGroup
  expanded: boolean
  onToggle: (gameId: string) => void
  trackedGame?: TrackedGame
  filter: BlueprintEventFilter
  sort: BlueprintEventSort
  // Reference timestamp for event statuses (stable per mount of the table).
  now: number
  // The game's blueprints are PC/console ones, whose events need `includePcConsole`.
  includePcConsole?: boolean
  // Opens the unlock dialog for a locked game (`group.isLocked`).
  onLockedGameClick?: (game: LockedGameSummary) => void
}

/**
 * One adopting game in the tree table: its 60px game row and, when expanded,
 * a row per game-level blueprint the game implements, each opening into its own
 * event rows. A game with a single implementation reads the same as one with
 * several rather than skipping the blueprint row. The game row carries
 * aggregates for the visible events; each block footer shows the longest break
 * and lets the user reveal the rest of the history past the first eight rows.
 */
const DrilldownGameRowsComponent: React.FC<DrilldownGameRowsProps> = ({
  rowId,
  blueprintId,
  blueprintName,
  eventTypeId,
  group,
  expanded,
  onToggle,
  trackedGame,
  filter,
  sort,
  now,
  includePcConsole,
  onLockedGameClick,
}) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const groupRowIdPrefix = useId()
  // Which of the game's blueprints are open. Every implementation gets its own row,
  // however many there are, so one game always reads the same way as the next.
  const [expandedBlueprintIds, setExpandedBlueprintIds] = useState<ReadonlySet<string>>(() => new Set())
  const toggleBlueprint = useCallback((blueprintId: string) => {
    setExpandedBlueprintIds((prev) => {
      const next = new Set(prev)
      if (next.has(blueprintId)) next.delete(blueprintId)
      else next.add(blueprintId)
      return next
    })
  }, [])

  // A game outside the user's plan still expands, but the backend serves none of its events,
  // so there is nothing to fetch: its blueprints are listed from the list payload, locked.
  const locked = Boolean(group.isLocked)
  const detailsExpanded = expanded && !locked

  const blueprintIds = useMemo(() => group.blueprints.map((item) => item.id), [group.blueprints])
  const details = useBlueprintDetails(blueprintId, blueprintIds, detailsExpanded, { includePcConsole })
  const isLoading = detailsExpanded && details.some((detail) => detail.isLoading)
  const isLoaded = detailsExpanded && details.length > 0 && details.every((detail) => detail.data)
  const failedBlueprintIds = useMemo(
    () => (detailsExpanded ? details.filter((detail) => detail.isError).map((detail) => detail.blueprintId) : []),
    [detailsExpanded, details]
  )
  // Nothing at all came back, so the row's aggregates would only be zeros.
  const noneLoaded = failedBlueprintIds.length > 0 && !details.some((detail) => detail.data)
  const retryBlueprintDetails = useRetryBlueprintDetails(blueprintId, { includePcConsole })
  const retryBlueprint = useCallback((id: string) => retryBlueprintDetails([id]), [retryBlueprintDetails])
  const retryFailedBlueprints = useCallback(() => retryBlueprintDetails(failedBlueprintIds), [retryBlueprintDetails, failedBlueprintIds])

  // Per-blueprint rows: every event (for chronological gaps) and the filtered + sorted subset.
  const blueprintMeta = useMemo(
    () =>
      group.blueprints.map((item) => {
        const detail = details.find((entry) => entry.blueprintId === item.id)
        const name = blueprintDisplayName(item, userLanguage)
        const allRows = detail?.data ? buildBlueprintEventRows(detail.data.liveEvents, name, now) : EMPTY_ROWS
        const loadFailed = Boolean(detail?.isError)
        // Stated on the row itself now, rather than looked up in the tracked game's
        // analyst-curated blueprint list.
        const isDiscontinued = Boolean(item.discontinued)
        const descriptionHtml = blueprintDescriptionHtml(item, userLanguage)
        return { item, name, allRows, loadFailed, isDiscontinued, descriptionHtml }
      }),
    [group.blueprints, details, userLanguage, now]
  )
  const blueprintGroups = useMemo(
    () => blueprintMeta.map((entry) => ({ ...entry, visibleRows: sortBlueprintEventRows(filterBlueprintEventRows(entry.allRows, filter), sort) })),
    [blueprintMeta, filter, sort]
  )
  const aggregate = useMemo(() => aggregateGameEvents(blueprintGroups), [blueprintGroups])
  const maxDurationDays = useMemo(
    () => blueprintGroups.reduce((max, entry) => entry.allRows.reduce((m, row) => Math.max(m, row.durationDays), max), 0),
    [blueprintGroups]
  )
  const earliestEventStart = useMemo(() => earliestVisibleRowStart(blueprintGroups), [blueprintGroups])
  // A blueprint the filter empties out is dropped rather than offered as a group row
  // that opens into nothing. One that failed to load stays, so it can be retried.
  const visibleGroups = useMemo(
    () => blueprintGroups.filter((entry) => filter === 'all' || entry.loadFailed || entry.visibleRows.length > 0),
    [blueprintGroups, filter]
  )

  // The game-level link always lands on the game's calendar grouped by blueprint, never on an event dialog.
  const gameCalendarPath = buildLiveEventsCalendarUrl({ gameId: group.gameId })

  const handleToggle = useCallback(() => onToggle(group.gameId), [onToggle, group.gameId])
  const stopPropagation = useCallback((event: React.SyntheticEvent) => event.stopPropagation(), [])

  const gameName = group.name || t('market-analytics:detail_panel_games_using_unknown_game')
  const gameDiscontinued = isGameGroupDiscontinued(group)
  // The list payload's count covers the blueprint's whole history, so under a filter it
  // is not the number this row is about to show. Events only arrive on expand, so say
  // nothing until they do rather than quote "312 events" ahead of a year that has four.
  // The list payload counts distinct events while the loaded rows are one per run, so
  // the two are labelled "events" and "event instances" rather than read as one number.
  // That list value sums every blueprint of the game, so it names how many went into it. They are the
  // blueprints of `group`, the same set `eventsCount` is summed over, and the standalone blueprints
  // count is dropped in that state rather than say the same thing twice. A blueprint the payload gave no
  // count for would be summed as zero, so the figure is held back (the blueprints count stays) rather than understate it.
  const listEventCountKnown = group.blueprints.every((item) => typeof item.liveEventsCount === 'number')
  const showsListEventCount = !isLoaded && filter === 'all' && listEventCountKnown
  const eventsCountLabel = isLoaded
    ? t('market-analytics:detail_panel_games_using_event_instance_count', { count: aggregate.visibleCount })
    : showsListEventCount
    ? t('market-analytics:detail_panel_games_using_event_count_across_blueprints', {
        events: t('market-analytics:detail_panel_games_using_event_count', { count: group.eventsCount }),
        blueprints: t('market-analytics:drilldown_blueprints_count', { count: group.blueprints.length }),
      })
    : ''
  const eventsCountTooltip = showsListEventCount ? t('market-analytics:detail_panel_games_using_event_count_tooltip') : ''
  // Same rule as the event count: the list payload's blueprints are only span-matched to a
  // year, so under a filter the loaded rows decide which of them actually ran in it.
  const blueprintsCount = isLoaded ? visibleGroups.length : filter === 'all' ? group.blueprints.length : null
  const blueprintsCountLabel =
    blueprintsCount === null || showsListEventCount ? '' : t('market-analytics:drilldown_blueprints_count', { count: blueprintsCount })
  // Until the rows are in there is no trustworthy first event, so the row keeps its collapsed wording.
  // Under a year the date is that year's first event (GR-3353), taken from the visible rows.
  const sinceLabel =
    expanded && !isLoading && earliestEventStart !== undefined
      ? t('market-analytics:event_table_since', {
          date: formatDate(new Date(earliestEventStart), { language: userLanguage, month: 'short', year: 'numeric' }),
        })
      : blueprintName

  // The same line as rendered below, as plain text: what the tooltip shows in full when the
  // cell is too narrow for the name or the line under it (GR-3254).
  const subtitleText = [group.artist, blueprintsCountLabel, eventsCountLabel, sinceLabel].filter(Boolean).join(' · ')
  const [nameRef, isNameTruncated] = useIsTextTruncated<HTMLElement>(gameName)
  const [subtitleRef, isSubtitleTruncated] = useIsTextTruncated<HTMLElement>(subtitleText)
  const isGameCellTruncated = isNameTruncated || isSubtitleTruncated

  const groupRowIdOf = (blueprintItemId: string) => `${groupRowIdPrefix}-${blueprintItemId}`
  const controlledGroupRowIds = isLoading ? '' : visibleGroups.map((entry) => groupRowIdOf(entry.item.id)).join(' ')

  return (
    <>
      <TableRow
        id={rowId}
        data-testid="drilldown-game-row"
        onClick={(event) => {
          if (!isNestedControlClick(event)) handleToggle()
        }}
        sx={(theme) => ({
          height: GAME_ROW_HEIGHT,
          cursor: 'pointer',
          ...(expanded ? expandedBlockRowSx(theme) : {}),
          '& > td': { borderBottomColor: expanded ? eventDividerColor(theme) : gameDividerColor(theme) },
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, expanded ? 0.06 : 0.04) },
        })}
      >
        <TableCell className={DRILLDOWN_CELL_CLASS.rank}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <DrilldownRowToggle
              expanded={expanded}
              controls={controlledGroupRowIds}
              label={t('market-analytics:drilldown_toggle_events_aria', { name: gameName })}
              onToggle={handleToggle}
            >
              <DisclosureIcon expanded={expanded} />
            </DrilldownRowToggle>
            {/* US grossing rank from the BE (`srank`); em dash = unranked (GR-2916). */}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {group.srank ?? '\u2014'}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell className={DRILLDOWN_CELL_CLASS.gameName}>
          <Stack direction="row" alignItems="center" spacing={GAME_ICON_GAP}>
            <GameIcon
              gameName={gameName}
              src={group.iconUrl}
              size="tiny"
              appType={group.appType}
              gamePlatforms={group.platforms}
              discontinued={gameDiscontinued}
              rootStyle={{
                width: GAME_ICON_SIZE,
                height: GAME_ICON_SIZE,
                // The skull badge hangs past the icon's corner, so the clip has to come off. GameIcon then rounds
                // the image and type plate by 22% rather than relying on the clip, so the box follows suit.
                borderRadius: gameDiscontinued ? GAME_ICON_DISCONTINUED_RADIUS : GAME_ICON_RADIUS,
                overflow: gameDiscontinued ? 'visible' : 'hidden',
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <GRTooltip
                content={
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                      {gameName}
                    </Typography>
                    {subtitleText ? (
                      <Typography variant="caption" display="block" sx={{ overflowWrap: 'anywhere' }}>
                        {subtitleText}
                      </Typography>
                    ) : null}
                    {eventsCountTooltip ? (
                      <Typography variant="caption" display="block" sx={{ overflowWrap: 'anywhere', mt: 0.5 }}>
                        {eventsCountTooltip}
                      </Typography>
                    ) : null}
                  </>
                }
                styles={{ display: 'block', minWidth: 0 }}
                tooltipProps={{
                  disableHoverListener: !isGameCellTruncated,
                  disableFocusListener: !isGameCellTruncated,
                  disableTouchListener: !isGameCellTruncated,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                  <Typography ref={nameRef} variant="body2" sx={{ fontWeight: 700 }} noWrap>
                    {gameName}
                  </Typography>
                  {locked ? <LockedGameIndicator /> : null}
                </Stack>
                <Typography ref={subtitleRef} variant="caption" color="text.secondary" noWrap display="block" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {group.artist ? `${group.artist} · ` : ''}
                  {blueprintsCountLabel ? (
                    <>
                      <Box component="span" sx={{ color: 'text.primary' }}>
                        {blueprintsCountLabel}
                      </Box>
                      {eventsCountLabel || sinceLabel ? ' · ' : ''}
                    </>
                  ) : null}
                  {eventsCountLabel ? (
                    <>
                      {/* A truncated cell already shows the whole line, hint included, in the cell's own tooltip. */}
                      <GRTooltip content={eventsCountTooltip} hidden={isGameCellTruncated} styles={{ cursor: 'help' }} tooltipProps={{ describeChild: true }}>
                        <Box component="span" sx={{ color: 'text.primary' }}>
                          {eventsCountLabel}
                        </Box>
                      </GRTooltip>
                      {sinceLabel ? ' · ' : ''}
                    </>
                  ) : null}
                  {sinceLabel}
                </Typography>
              </GRTooltip>
            </Box>
          </Stack>
        </TableCell>
        {expanded ? (
          <>
            <TableCell />
            <TableCell />
            <TableCell>
              {isLoading ? (
                <Skeleton variant="text" width={48} />
              ) : aggregate.avgDurationDays !== null ? (
                <AverageDays days={aggregate.avgDurationDays} />
              ) : null}
            </TableCell>
            <TableCell>
              {isLoading ? <Skeleton variant="text" width={40} /> : aggregate.avgGapDays !== null ? <AverageDays days={aggregate.avgGapDays} /> : null}
            </TableCell>
            <TableCell>
              {isLoading ? (
                <Skeleton variant="text" width={48} />
              ) : noneLoaded ? null : (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <StatusDot color={aggregate.liveCount > 0 ? 'success.main' : 'text.disabled'} pulsing={aggregate.liveCount > 0} />
                  <Typography variant="caption" color="text.secondary" component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {t('market-analytics:event_table_live_count', { count: aggregate.liveCount })}
                  </Typography>
                </Stack>
              )}
            </TableCell>
            <TableCell align="right" className={DRILLDOWN_CELL_CLASS.actions}>
              {locked ? null : (
                <GRTooltip content={t('market-analytics:open_in_live_events_calendar')} styles={{ display: 'inline-flex' }}>
                  <IconButton
                    component={RouterLink}
                    to={gameCalendarPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    aria-label={t('market-analytics:open_in_live_events_calendar')}
                    onClick={stopPropagation}
                    sx={{ color: 'primary.main', p: 0 }}
                  >
                    <OpenInNew sx={{ fontSize: EVENT_ROW_ICON_SIZE }} />
                  </IconButton>
                </GRTooltip>
              )}
            </TableCell>
          </>
        ) : (
          emptyCells(DRILLDOWN_COLUMN_COUNT - 2)
        )}
      </TableRow>
      {detailsExpanded && isLoading ? <EventRowsSkeleton /> : null}
      {expanded && (locked || !isLoading)
        ? (locked ? blueprintGroups : visibleGroups).map((entry) => (
            <BlueprintEventGroupRows
              key={entry.item.id}
              rowId={groupRowIdOf(entry.item.id)}
              item={entry.item}
              name={entry.name}
              rows={entry.visibleRows}
              allRows={entry.allRows}
              eventInstanceCount={filter === 'all' ? entry.allRows.length : entry.visibleRows.length}
              expanded={expandedBlueprintIds.has(entry.item.id)}
              onToggle={toggleBlueprint}
              loadFailed={entry.loadFailed}
              onRetry={retryBlueprint}
              isDiscontinued={entry.isDiscontinued}
              descriptionHtml={entry.descriptionHtml}
              maxDurationDays={maxDurationDays}
              gameId={group.gameId}
              eventTypeId={eventTypeId}
              trackedGame={trackedGame}
              userLanguage={userLanguage}
              locked={locked}
              onLockedClick={onLockedGameClick ? () => onLockedGameClick({ name: group.name, artist: group.artist, iconUrl: group.iconUrl }) : undefined}
            />
          ))
        : null}
      {detailsExpanded && !isLoading && failedBlueprintIds.length > 0 ? (
        <TableRow
          data-testid="drilldown-load-error"
          sx={(theme) => ({ ...expandedBlockRowSx(theme), height: EVENT_ROW_HEIGHT, '& > td': { borderBottomColor: eventDividerColor(theme) } })}
        >
          <TableCell className={DRILLDOWN_CELL_CLASS.rank} />
          <TableCell colSpan={DRILLDOWN_COLUMN_COUNT - 1} className={DRILLDOWN_CELL_CLASS.eventName}>
            <Stack role="alert" direction="row" alignItems="center" columnGap={1.5} flexWrap="wrap">
              <Typography variant="body2" color="warning.main">
                {noneLoaded ? t('market-analytics:event_table_game_load_error') : t('market-analytics:event_table_partial_load_error')}
              </Typography>
              <Button variant="text" size="small" onClick={retryFailedBlueprints} sx={inlineTextButtonSx}>
                {t('market-analytics:retry')}
              </Button>
            </Stack>
          </TableCell>
        </TableRow>
      ) : detailsExpanded && !isLoading && aggregate.visibleCount === 0 ? (
        <TableRow sx={(theme) => ({ ...expandedBlockRowSx(theme), height: EVENT_ROW_HEIGHT, '& > td': { borderBottomColor: eventDividerColor(theme) } })}>
          <TableCell className={DRILLDOWN_CELL_CLASS.rank} />
          <TableCell colSpan={DRILLDOWN_COLUMN_COUNT - 1} className={DRILLDOWN_CELL_CLASS.eventName}>
            <Typography variant="body2" color="text.secondary">
              {t('market-analytics:event_table_no_matches')}
            </Typography>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}

export const DrilldownGameRows = React.memo(DrilldownGameRowsComponent)
DrilldownGameRows.displayName = 'DrilldownGameRows'
